import { LitElement, html, css } from "lit";
import { customElement, property } from "lit/decorators.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";

@customElement("fediverse-status")
export class FediverseStatus extends LitElement {
  static styles = [
    css`
      :host {
        display: flex;
        flex-direction: column;
        border: 1px solid hsl(var(--neutral-900) / 25%);

      }
      
      :host([type="reply"]) {
        border: none;

        .fediverse__statistics {
          opacity: 0.75;
        }

        .row,
        .fediverse__status-content,
        header,
        footer {
          padding-inline: 0;
        }

        header {
          padding-block-end: 0;
        }

        .fediverse__avatar {
          border-radius: 1000px;
        }
      }

        .fediverse__pubdate {
          font-family: var(--font-display);
          font-style: normal;
          letter-spacing: calc(1em * -0.08);
          letter-spacing: -8%;
          text-decoration: none;
          flex: 2 1 20ch;

          &:is(a[href]) {
            color: inherit;

            &:hover {
              text-decoration: underline;
            }
          }
        }

      .visually-hidden {
          border: 0;
          clip: rect(0 0 0 0);
          height: auto;
          margin: 0;
          overflow: hidden;
          padding: 0;
          position: absolute !important;
          width: 1px;
          white-space: nowrap;
        }

        .icon {
          font-style: normal;
          font-family: 'Material Symbols Sharp';
          font-variation-settings:
            'FILL' 0,
            'wght' 500,
            'GRAD' -25,
            'opsz' 40;
          font-size: 1em;
          line-height: 1;
        }

      header {
        align-items: center;
        display: flex;
        gap: 1ch;
        padding: 1rem;
      }

      .fediverse__avatar {
        aspect-ratio: 1;
        object-fit: cover;
        border: none;
        width: 2rem;
        height: auto;
        margin: 0;
      }

      .fediverse__display-name {
        align-items: center;
        color: inherit;
        display: inline-block;
        font-family: var(--font-display);
        font-size: 1.2rem;
        font-style: normal;
        gap: 1ch;
        letter-spacing: -.04em;
        overflow: hidden;
        text-decoration: none;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .fediverse__username {
        color: inherit;
        font-family: var(--font-serif);
        font-size: smaller;
        font-style: italic;
        letter-spacing: -.04em;
        opacity: .8;
        overflow: hidden;
        text-decoration: underline;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .row {
        align-items: center;
        display: flex;
        justify-content: space-between;
        padding: 1ch;
        flex-wrap: wrap;

        &:has(.fediverse__statisticcs) {
          flex-wrap: wrap;
        }
      }
      
      .fediverse__statistics {
        flex-wrap: wrap;
        align-items: center;
        justify-content: end;
        display: flex;
        gap: clamp(0.5rem, 2vw, 1rem);
        flex: 1 1 20ch;


        p {
          margin: 0;
          display: flex;
          align-items: center;
          gap: 1ch;
        }
      }

      .stat-label {
        font-family: var(--font-display);
        font-size: .9em;
        opacity: .7;
        text-transform: uppercase;
      }

      .interactive {
        background-color: hsl(var(--neutral-900)/5%);
        border: 1px solid hsl(var(--neutral-900)/25%);
        display: block;
        font-family: var(--font-display);
        font-style: normal;
        letter-spacing: -.04em;
        padding: .5em 1em;
        text-decoration: none;
        flex: 1;
        color: inherit;
      }

      .fediverse__status-content {
        padding-inline: 2rem;
        padding-block: 1rem;
        letter-spacing: -0.02em;
      }

      img.emoji {
        display: inline-block;
        width: auto;
        height: 1em;
      }
    `
  ];
  @property({ type: String })
  accessor username: string = "";

  @property({ type: String })
  accessor displayname: string = "";

  @property({ type: String })
  accessor instance: string = "";

  @property({ type: String })
  accessor avatarsrc: string = "";

  @property({ type: String })
  accessor pubdatetime: string = new Date().toISOString()

  @property({ type: String })
  accessor href: string = "";

  @property({ type: Number })
  accessor repliescount: number = 0;

  @property({ type: Number })
  accessor reblogscount: number = 0;

  @property({ type: Number })
  accessor likescount: number = 0;

  @property({ type: String })
  accessor type: string = "main"

  @property({ type: String })
  accessor accounturl: string = ""

  @property({ type: Boolean })
  accessor discoverable: boolean = false

  @property({ type: Boolean })
  accessor nested: boolean = false

  render() {
    const formatter = new Intl.DateTimeFormat('en-US', { dateStyle: 'long' })
    return html`
      <header>
        <img class="fediverse__avatar" src="${this.avatarsrc}" alt="${this.username}'s avatar" width="48" height="48" />
        <a class="fediverse__display-name" href="${ifDefined(this.discoverable ? this.accounturl : undefined)}" target="_blank" rel="noopener">
          ${unsafeHTML(this.displayname)}
        </a>
        ${this.discoverable
        ? html`<a class="fediverse__username" href="${this.accounturl}" target="_blank" rel="noopener">@${this.username}${this.type === 'main' ? html`@${this.instance}` : ''}</a>`
        : ''
      }
      </header>
      <div class="fediverse__status-content">
        <slot name="statuscontent"></slot>
      </div>
      <footer>
        <div class="row">
          <a class="fediverse__pubdate" href="${this.href}" target="_blank" rel="noopener"><time datetime="${this.pubdatetime}">${formatter.format(new Date(this.pubdatetime))}</time></a>
          <div class="fediverse__statistics">
            <p>
              <b>${this.repliescount}</b> 
              <span class="${this.type === 'main' ? 'stat-label' : 'visually-hidden'}">replies</span>
              ${this.type != 'main' ? html`<i class="icon">mode_comment</i>` : ''}
            </p>
            <p>
              <b>${this.reblogscount}</b> 
              <span class="${this.type === 'main' ? 'stat-label' : 'visually-hidden'}">reblogs</span>
              ${this.type != 'main' ? html`<i class="icon">repeat</i>` : ''}
            </p>
            <p>
              <b>${this.likescount}</b> 
              <span class="${this.type === 'main' ? 'stat-label' : 'visually-hidden'}">likes</span>
              ${this.type != 'main' ? html`<i class="icon">star</i>` : ''}
            </p>
          </div>
        </div>
        ${this.type === 'main' ? html`<div class="row">
          <a href="${this.href}" class="interactive" target="_blank" rel="noopener">Leave a comment… <small>(requires a Fediverse account)</small></a>
        </div>`: ''}
      </footer>
    `;
  }
}